import { HostSchedule } from "../api/meetings";
import { Host, hostLabel, resolveHost } from "./host";

export interface MeetingHostSchedule extends Record<string, Host | undefined> {
  monday?: Host;
  tuesday?: Host;
  wednesday?: Host;
  thursday?: Host;
  friday?: Host;
  saturday?: Host;
  sunday?: Host;
  default?: Host;
}

export interface MeetingHostIdSchedule
  extends Record<string, string | undefined> {
  monday?: string;
  tuesday?: string;
  wednesday?: string;
  thursday?: string;
  friday?: string;
  saturday?: string;
  sunday?: string;
  default?: string;
}

const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const scheduleKeys = ["default", ...days];

/**
 * Ids the picker cannot name are kept as placeholder hosts (see
 * `unknownHost`), never dropped: a re-save must send every id back as it was.
 */
export const fromApiHostSchedule = (
  apiHostSchedule: HostSchedule | undefined,
  hostsById: Record<string, Host>,
): MeetingHostSchedule | undefined => {
  if (!apiHostSchedule) {
    return undefined;
  }
  const schedule: MeetingHostSchedule = {};
  for (const key of scheduleKeys) {
    const id = apiHostSchedule[key as keyof HostSchedule];
    if (id) {
      schedule[key] = resolveHost(id, hostsById);
    }
  }
  return schedule;
};

export const toDisplayString = (hosts?: MeetingHostSchedule) => {
  if (!hosts) {
    return "(no host set)";
  }
  if (!hosts.default) {
    throw new Error("Default host must be set");
  }
  const overrides = days
    .filter((day) => hosts[day])
    .map((day) => {
      const host = hosts[day];
      if (!host) {
        throw new Error("Host must be set");
      }
      return `${day.slice(0, 2).toLocaleUpperCase()}: ${hostLabel(host)}`;
    })
    .join(", ");
  const overrideStr = overrides.length > 0 ? ` (${overrides})` : "";
  return `${hostLabel(hosts.default)}${overrideStr}`;
};

export const toMeetingHostIdSchedule = (
  hosts: MeetingHostSchedule,
): MeetingHostIdSchedule => {
  const dailyIds: MeetingHostIdSchedule = {};
  for (const day of days) {
    if (hosts[day]) {
      dailyIds[day] = hosts[day]!.id;
    }
  }
  if (hosts.default) {
    dailyIds.default = hosts.default.id;
  }
  return dailyIds;
};

export const areEquivalent = (
  a?: MeetingHostSchedule,
  b?: MeetingHostSchedule,
) => {
  if (!a || !b) {
    return a === b;
  }
  return a.default === b.default && days.every((day) => a[day] === b[day]);
};
